import type {
  AzureCpuRentalRow,
  ComputeRentalGpuRow,
  ComputeRentalPanel,
  ComputeRentalSnapshot,
  CpuRentalTier,
  GpuRentalHistoryDataset,
  GpuRentalHistoryPoint,
  RentalHistoryProvider,
  RentalGpuSegment,
  RentalStockStatus,
  RunpodRentalQuote,
  VastRentalQuote,
} from "../types";

type JsonRecord = Record<string, unknown>;

interface RentalGpuCatalogRow {
  sku: string;
  label: string;
  segment: RentalGpuSegment;
  vramGb: number;
  runpodIds: string[];
  matchesVast: (name: string, vramGb: number) => boolean;
}

interface AzureCpuCatalogRow {
  sku: string;
  label: string;
  tier: CpuRentalTier;
  vcpus: number;
  memoryGb: number;
}

export const RENTAL_GPU_CATALOG: readonly RentalGpuCatalogRow[] = [
  { sku: "b300", label: "B300", segment: "frontier", vramGb: 288, runpodIds: ["NVIDIA B300 SXM6 AC"], matchesVast: (name) => name === "B300" },
  { sku: "b200", label: "B200", segment: "frontier", vramGb: 180, runpodIds: ["NVIDIA B200"], matchesVast: (name) => name === "B200" },
  { sku: "mi300x", label: "MI300X", segment: "frontier", vramGb: 192, runpodIds: ["AMD Instinct MI300X OAM"], matchesVast: (name) => name.includes("MI300X") },
  { sku: "h200-sxm", label: "H200 SXM", segment: "frontier", vramGb: 141, runpodIds: ["NVIDIA H200"], matchesVast: (name) => name === "H200" },
  { sku: "h200-nvl", label: "H200 NVL", segment: "frontier", vramGb: 143, runpodIds: ["NVIDIA H200 NVL"], matchesVast: (name) => name === "H200 NVL" },
  { sku: "h100-sxm", label: "H100 SXM", segment: "frontier", vramGb: 80, runpodIds: ["NVIDIA H100 80GB HBM3"], matchesVast: (name) => name === "H100 SXM" },
  { sku: "h100-nvl", label: "H100 NVL", segment: "frontier", vramGb: 94, runpodIds: ["NVIDIA H100 NVL"], matchesVast: (name) => name === "H100 NVL" },
  { sku: "h100-pcie", label: "H100 PCIe", segment: "frontier", vramGb: 80, runpodIds: ["NVIDIA H100 PCIe"], matchesVast: (name) => name === "H100 PCIE" },
  { sku: "a100-sxm-80", label: "A100 SXM 80GB", segment: "inference", vramGb: 80, runpodIds: ["NVIDIA A100-SXM4-80GB"], matchesVast: (name, vram) => name === "A100 SXM4" && vram >= 70 },
  { sku: "a100-pcie-80", label: "A100 PCIe 80GB", segment: "inference", vramGb: 80, runpodIds: ["NVIDIA A100 80GB PCIe"], matchesVast: (name, vram) => name === "A100 PCIE" && vram >= 70 },
  { sku: "a100-40", label: "A100 40GB", segment: "inference", vramGb: 40, runpodIds: ["NVIDIA A100-SXM4-40GB"], matchesVast: (name, vram) => (name === "A100 SXM4" || name === "A100 PCIE") && vram < 70 },
  { sku: "l40s", label: "L40S", segment: "inference", vramGb: 48, runpodIds: ["NVIDIA L40S"], matchesVast: (name) => name === "L40S" },
  { sku: "rtx-5090", label: "RTX 5090", segment: "consumer", vramGb: 32, runpodIds: ["NVIDIA GeForce RTX 5090"], matchesVast: (name) => name === "RTX 5090" },
  { sku: "rtx-4090", label: "RTX 4090", segment: "consumer", vramGb: 24, runpodIds: ["NVIDIA GeForce RTX 4090"], matchesVast: (name) => name === "RTX 4090" },
] as const;

/** Fixed East US Linux VM basket. Stable specs make total and unit prices comparable over time. */
export const AZURE_CPU_CATALOG: readonly AzureCpuCatalogRow[] = [
  { sku: "Standard_D2as_v5", label: "D2as v5", tier: "general", vcpus: 2, memoryGb: 8 },
  { sku: "Standard_D4as_v5", label: "D4as v5", tier: "general", vcpus: 4, memoryGb: 16 },
  { sku: "Standard_D8as_v5", label: "D8as v5", tier: "general", vcpus: 8, memoryGb: 32 },
  { sku: "Standard_D16as_v5", label: "D16as v5", tier: "general", vcpus: 16, memoryGb: 64 },
  { sku: "Standard_D32as_v5", label: "D32as v5", tier: "general", vcpus: 32, memoryGb: 128 },
  { sku: "Standard_E2as_v5", label: "E2as v5", tier: "memory", vcpus: 2, memoryGb: 16 },
  { sku: "Standard_E4as_v5", label: "E4as v5", tier: "memory", vcpus: 4, memoryGb: 32 },
  { sku: "Standard_E8as_v5", label: "E8as v5", tier: "memory", vcpus: 8, memoryGb: 64 },
  { sku: "Standard_E16as_v5", label: "E16as v5", tier: "memory", vcpus: 16, memoryGb: 128 },
  { sku: "Standard_E32as_v5", label: "E32as v5", tier: "memory", vcpus: 32, memoryGb: 256 },
] as const;

export const COMPUTE_RENTAL_HISTORY_DAYS = 90;
export const GPU_RENTAL_BACKFILL_DAYS = 180;
export const VAST_RELIABILITY_FLOOR = 0.95;
export const VAST_QUERY_LIMIT = 5000;

const STOCK_STATUSES = new Set<RentalStockStatus>(["High", "Medium", "Low", "None"]);
const FORBIDDEN_PUBLIC_FIELDS = [
  "public_ipaddr",
  "host_id",
  "machine_id",
  "gpu_ids",
  "ask_contract_id",
  "bundle_id",
] as const;

const GETFLOPS_GPU_NAMES: Readonly<Partial<Record<string, string>>> = {
  b200: "B200",
  "h200-sxm": "H200",
  "h200-nvl": "H200 NVL",
  "h100-sxm": "H100 SXM",
  "h100-nvl": "H100 NVL",
  "h100-pcie": "H100 PCIE",
  "a100-sxm-80": "A100 SXM4",
  "a100-pcie-80": "A100 PCIE",
  "rtx-5090": "RTX 5090",
  "rtx-4090": "RTX 4090",
} as const;

const GETFLOPS_PROVIDERS: ReadonlyArray<{ provider: RentalHistoryProvider; sourceProvider: string; pricingType: string }> = [
  { provider: "vast", sourceProvider: "vast.ai", pricingType: "on-demand" },
  { provider: "runpod-community", sourceProvider: "runpod", pricingType: "community" },
  { provider: "runpod-secure", sourceProvider: "runpod", pricingType: "on-demand" },
] as const;

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function assertCalendarDate(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error(`${label} must be YYYY-MM-DD.`);
  const parsed = new Date(`${value}T00:00:00Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) throw new Error(`${label} is not a calendar date.`);
}

function assertTimestamp(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || Number.isNaN(Date.parse(value))) throw new Error(`${label} must be an ISO timestamp.`);
}

function finite(value: unknown, label: string, options: { min?: number; max?: number; integer?: boolean } = {}) {
  if (!Number.isFinite(value)) throw new Error(`${label} must be finite.`);
  const number = Number(value);
  if (options.integer && !Number.isInteger(number)) throw new Error(`${label} must be an integer.`);
  if (options.min != null && number < options.min) throw new Error(`${label} must be at least ${options.min}.`);
  if (options.max != null && number > options.max) throw new Error(`${label} must be at most ${options.max}.`);
  return number;
}

function nullableFinite(value: unknown, label: string, transform: (number: number) => number) {
  if (value == null) return null;
  const number = finite(value, label, { min: 0 });
  return number > 0 ? transform(number) : null;
}

export function quantile(values: number[], probability: number) {
  if (!values.length) throw new Error("Quantile requires at least one value.");
  if (!Number.isFinite(probability) || probability < 0 || probability > 1) throw new Error("Quantile probability must be between zero and one.");
  const sorted = [...values].sort((left, right) => left - right);
  const position = (sorted.length - 1) * probability;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

function medianOrNull(values: Array<number | null>) {
  const usable = values.filter((value): value is number => value != null && Number.isFinite(value));
  return usable.length ? quantile(usable, 0.5) : null;
}

function parseRunpodQuote(value: unknown, label: string): RunpodRentalQuote | null {
  if (value == null) return null;
  if (!isRecord(value)) throw new Error(`${label} must be an object or null.`);
  const price = value.uninterruptablePrice;
  const stock = value.stockStatus;
  if (price == null && stock == null) return null;
  if (!Number.isFinite(price) || Number(price) <= 0) throw new Error(`${label} has an invalid on-demand price.`);
  if (typeof stock !== "string" || !STOCK_STATUSES.has(stock as RentalStockStatus)) throw new Error(`${label} has an invalid stock status.`);
  let availableGpuCounts: number[] | null = null;
  if (value.availableGpuCounts != null) {
    if (!Array.isArray(value.availableGpuCounts)) throw new Error(`${label} availableGpuCounts must be an array or null.`);
    availableGpuCounts = value.availableGpuCounts.map((count, index) => finite(count, `${label} availableGpuCounts[${index}]`, { min: 1, integer: true }));
    if (new Set(availableGpuCounts).size !== availableGpuCounts.length) throw new Error(`${label} availableGpuCounts contains duplicates.`);
    availableGpuCounts.sort((left, right) => left - right);
  }
  return { stockStatus: stock as RentalStockStatus, pricePerGpuHourUsd: Number(price), availableGpuCounts };
}

interface ParsedVastOffer {
  catalog: RentalGpuCatalogRow | null;
  numGpus: number;
  pricePerGpuHour: number;
  effectiveVcpuPerGpu: number | null;
  cpuRamGbPerGpu: number | null;
  bundleUsdPerEffectiveVcpuHour: number | null;
  dlperfPerDollar: number | null;
  flopsPerDollar: number | null;
  reliabilityPct: number;
}

function parseVastOffers(value: unknown) {
  if (!isRecord(value) || !Array.isArray(value.offers) || value.offers.length === 0) throw new Error("Vast offer search returned an invalid or empty payload.");
  const parsed: ParsedVastOffer[] = value.offers.map((item, index) => {
    if (!isRecord(item)) throw new Error(`Vast offer ${index + 1} is not an object.`);
    if (item.verification !== "verified" || item.rentable !== true || item.rented !== false || item.resource_type !== "gpu") {
      throw new Error(`Vast offer ${index + 1} violates the verified/rentable GPU query contract.`);
    }
    if (typeof item.gpu_name !== "string" || !item.gpu_name.trim()) throw new Error(`Vast offer ${index + 1} has no GPU name.`);
    const numGpus = finite(item.num_gpus, `Vast offer ${index + 1} num_gpus`, { min: 1, integer: true });
    const dphTotal = finite(item.dph_total, `Vast offer ${index + 1} dph_total`, { min: Number.MIN_VALUE });
    const gpuRamMb = finite(item.gpu_ram, `Vast offer ${index + 1} gpu_ram`, { min: 1 });
    const reliability = finite(item.reliability, `Vast offer ${index + 1} reliability`, { min: 0, max: 1 });
    const vramGb = gpuRamMb / 1024;
    const catalog = RENTAL_GPU_CATALOG.find((row) => row.matchesVast(item.gpu_name as string, vramGb)) ?? null;
    const cpuCores = nullableFinite(item.cpu_cores_effective, `Vast offer ${index + 1} cpu_cores_effective`, (number) => number);
    return {
      catalog,
      numGpus,
      pricePerGpuHour: dphTotal / numGpus,
      effectiveVcpuPerGpu: cpuCores == null ? null : cpuCores / numGpus,
      cpuRamGbPerGpu: nullableFinite(item.cpu_ram, `Vast offer ${index + 1} cpu_ram`, (number) => number / 1024 / numGpus),
      bundleUsdPerEffectiveVcpuHour: cpuCores == null ? null : dphTotal / cpuCores,
      dlperfPerDollar: nullableFinite(item.dlperf_per_dphtotal, `Vast offer ${index + 1} dlperf_per_dphtotal`, (number) => number),
      flopsPerDollar: nullableFinite(item.flops_per_dphtotal, `Vast offer ${index + 1} flops_per_dphtotal`, (number) => number),
      reliabilityPct: reliability * 100,
    };
  });
  const reliable = parsed.filter((offer) => offer.reliabilityPct >= VAST_RELIABILITY_FLOOR * 100);
  if (!reliable.length) throw new Error("Vast offer search has no offers meeting the reliability floor.");
  return { sourceCount: value.offers.length, reliable };
}

function aggregateVast(offers: ParsedVastOffer[]): Map<string, VastRentalQuote> {
  const grouped = new Map<string, ParsedVastOffer[]>();
  for (const offer of offers) {
    if (!offer.catalog) continue;
    grouped.set(offer.catalog.sku, [...(grouped.get(offer.catalog.sku) ?? []), offer]);
  }
  return new Map([...grouped.entries()].map(([sku, rows]) => {
    const prices = rows.map((row) => row.pricePerGpuHour);
    return [sku, {
      offerCount: rows.length,
      gpuCount: rows.reduce((sum, row) => sum + row.numGpus, 0),
      pricePerGpuHourUsd: {
        min: quantile(prices, 0),
        p25: quantile(prices, 0.25),
        median: quantile(prices, 0.5),
        p75: quantile(prices, 0.75),
      },
      medianEffectiveVcpuPerGpu: medianOrNull(rows.map((row) => row.effectiveVcpuPerGpu)),
      medianCpuRamGbPerGpu: medianOrNull(rows.map((row) => row.cpuRamGbPerGpu)),
      medianBundleUsdPerEffectiveVcpuHour: medianOrNull(rows.map((row) => row.bundleUsdPerEffectiveVcpuHour)),
      medianDlperfPerDollar: medianOrNull(rows.map((row) => row.dlperfPerDollar)),
      medianFlopsPerDollar: medianOrNull(rows.map((row) => row.flopsPerDollar)),
      medianReliabilityPct: quantile(rows.map((row) => row.reliabilityPct), 0.5),
    } satisfies VastRentalQuote];
  }));
}

function parseRunpodGpuTypes(value: unknown) {
  if (!isRecord(value) || (Array.isArray(value.errors) && value.errors.length > 0) || !isRecord(value.data) || !Array.isArray(value.data.gpuTypes)) {
    throw new Error("RunPod GPU types GraphQL payload is invalid or contains errors.");
  }
  if (value.data.gpuTypes.length < 20) throw new Error("RunPod GPU type inventory is unexpectedly short.");
  const byId = new Map<string, JsonRecord>();
  for (const [index, item] of value.data.gpuTypes.entries()) {
    if (isRecord(item) && item.id === "unknown" && item.displayName === "unknown" && item.memoryInGb === 0
      && isRecord(item.community) && item.community.stockStatus == null && item.community.uninterruptablePrice == null
      && isRecord(item.secure) && item.secure.stockStatus == null && item.secure.uninterruptablePrice == null) {
      continue;
    }
    if (!isRecord(item) || typeof item.id !== "string" || !item.id || typeof item.displayName !== "string" || !item.displayName
      || !Number.isFinite(item.memoryInGb) || Number(item.memoryInGb) <= 0 || !Object.prototype.hasOwnProperty.call(item, "community")
      || !Object.prototype.hasOwnProperty.call(item, "secure")) {
      throw new Error(`RunPod GPU type ${index + 1} is invalid.`);
    }
    if (byId.has(item.id)) throw new Error(`RunPod GPU type ${item.id} is duplicated.`);
    byId.set(item.id, item);
  }
  return { byId, count: byId.size };
}

function firstRunpodRow(byId: Map<string, JsonRecord>, ids: string[]) {
  return ids.map((id) => byId.get(id)).find(Boolean) ?? null;
}

function parseAzureCpuRows(payloads: unknown[], capturedAt: string): AzureCpuRentalRow[] {
  if (payloads.length !== AZURE_CPU_CATALOG.length) {
    throw new Error(`Azure CPU price fetch returned ${payloads.length} payloads; expected ${AZURE_CPU_CATALOG.length}.`);
  }
  const items = payloads.flatMap((payload, payloadIndex) => {
    if (!isRecord(payload) || !Array.isArray(payload.Items) || payload.Items.length === 0) {
      throw new Error(`Azure CPU price payload ${payloadIndex + 1} is invalid or empty.`);
    }
    if (payload.NextPageLink != null && (typeof payload.NextPageLink !== "string" || !payload.NextPageLink)) {
      throw new Error(`Azure CPU price payload ${payloadIndex + 1} has an invalid NextPageLink.`);
    }
    return payload.Items;
  });
  const capturedTime = Date.parse(capturedAt);
  return AZURE_CPU_CATALOG.map((catalog) => {
    const candidates = items.filter((item): item is JsonRecord => {
      if (!isRecord(item) || item.armSkuName !== catalog.sku || item.armRegionName !== "eastus"
        || item.serviceName !== "Virtual Machines" || item.currencyCode !== "USD"
        || item.unitOfMeasure !== "1 Hour" || item.type !== "Consumption" || item.isPrimaryMeterRegion !== true
        || typeof item.productName !== "string" || !item.productName.startsWith("Virtual Machines ")
        || item.productName.includes("Windows") || typeof item.skuName !== "string" || typeof item.meterName !== "string"
        || /Spot|Low Priority/i.test(item.skuName) || /Spot|Low Priority/i.test(item.meterName)
        || !Number.isFinite(item.retailPrice) || Number(item.retailPrice) <= 0
        || typeof item.effectiveStartDate !== "string" || Number.isNaN(Date.parse(item.effectiveStartDate))) return false;
      return Date.parse(item.effectiveStartDate) <= capturedTime;
    }).sort((left, right) => Date.parse(right.effectiveStartDate as string) - Date.parse(left.effectiveStartDate as string));
    if (!candidates.length) throw new Error(`Azure CPU price payload has no current Linux on-demand quote for ${catalog.sku}.`);
    const latestStart = candidates[0].effectiveStartDate;
    const latest = candidates.filter((item) => item.effectiveStartDate === latestStart);
    if (latest.length !== 1) throw new Error(`Azure CPU price payload has duplicate current quotes for ${catalog.sku}.`);
    const price = Number(latest[0].retailPrice);
    return {
      provider: "azure",
      region: "eastus",
      tier: catalog.tier,
      sku: catalog.sku,
      label: catalog.label,
      vcpus: catalog.vcpus,
      memoryGb: catalog.memoryGb,
      pricePerHourUsd: price,
      pricePerVcpuHourUsd: price / catalog.vcpus,
      pricePerMemoryGbHourUsd: price / catalog.memoryGb,
      effectiveStartDate: new Date(latestStart as string).toISOString(),
    };
  });
}

export function buildGpuRentalHistoryDataset(payload: unknown, capturedAt: string): GpuRentalHistoryDataset {
  if (!isRecord(payload)) throw new Error("Getflops GPU history payload must be an object.");
  assertTimestamp(capturedAt, "Getflops capture timestamp");
  const captureTime = Date.parse(capturedAt);
  const series: GpuRentalHistoryDataset["series"] = [];
  for (const catalog of RENTAL_GPU_CATALOG) {
    const sourceGpu = GETFLOPS_GPU_NAMES[catalog.sku];
    if (!sourceGpu) continue;
    for (const provider of GETFLOPS_PROVIDERS) {
      const sourceKey: string = `${sourceGpu}|${provider.sourceProvider}|${provider.pricingType}`;
      const rawSeries: unknown = payload[sourceKey];
      if (rawSeries == null) continue;
      if (!Array.isArray(rawSeries) || rawSeries.length === 0) throw new Error(`Getflops history ${sourceKey} is invalid or empty.`);
      const byDate = new Map<string, GpuRentalHistoryPoint>();
      for (const [index, rawPoint] of (rawSeries as unknown[]).entries()) {
        const label = `Getflops history ${sourceKey} row ${index + 1}`;
        if (!isRecord(rawPoint) || rawPoint.gpu_name !== sourceGpu || rawPoint.provider !== provider.sourceProvider
          || rawPoint.pricing_type !== provider.pricingType || rawPoint.region !== null) {
          throw new Error(`${label} identity does not match its series key.`);
        }
        assertTimestamp(rawPoint.fetched_at, `${label} fetched_at`);
        if (Date.parse(rawPoint.fetched_at) > captureTime + 5 * 60_000) throw new Error(`${label} is newer than the capture timestamp.`);
        const date = (rawPoint.fetched_at as string).slice(0, 10);
        assertCalendarDate(date, `${label} date`);
        const min = finite(rawPoint.min, `${label} min`, { min: Number.MIN_VALUE });
        const median = finite(rawPoint.median, `${label} median`, { min: Number.MIN_VALUE });
        const max = rawPoint.max == null ? null : finite(rawPoint.max, `${label} max`, { min: Number.MIN_VALUE });
        const offerCount = finite(rawPoint.num_offers, `${label} num_offers`, { min: 1, integer: true });
        if (min > median || (max != null && median > max)) throw new Error(`${label} price distribution is invalid.`);
        const next: GpuRentalHistoryPoint = {
          date,
          capturedAt: new Date(rawPoint.fetched_at as string).toISOString(),
          minPricePerGpuHourUsd: min,
          medianPricePerGpuHourUsd: median,
          maxPricePerGpuHourUsd: max,
          offerCount,
        };
        const prior = byDate.get(date);
        if (!prior || prior.capturedAt < next.capturedAt) byDate.set(date, next);
      }
      const points = [...byDate.values()]
        .sort((left, right) => left.date.localeCompare(right.date))
        .slice(-GPU_RENTAL_BACKFILL_DAYS);
      if (points.length >= 2) series.push({ sku: catalog.sku, provider: provider.provider, points });
    }
  }
  if (!series.some((item) => item.sku === "h100-sxm" && item.provider === "vast")
    || !series.some((item) => item.sku === "h100-sxm" && item.provider === "runpod-secure")) {
    throw new Error("Getflops GPU history lacks H100 SXM Vast or RunPod on-demand coverage.");
  }
  const dates = series.flatMap((item) => item.points.map((point) => point.date));
  const dataset: GpuRentalHistoryDataset = {
    source: "getflops",
    sourceUrl: "https://www.getflops.ai/api/history_all",
    attribution: "Historical GPU pricing: getflops.ai",
    firstDate: dates.sort()[0],
    lastDate: dates.sort().at(-1)!,
    series,
  };
  assertSafeAggregate(dataset);
  return dataset;
}

function assertSafeAggregate(value: unknown) {
  const serialized = JSON.stringify(value);
  for (const field of FORBIDDEN_PUBLIC_FIELDS) {
    if (serialized.includes(`"${field}"`)) throw new Error(`Rental aggregate leaked forbidden field ${field}.`);
  }
}

export function buildComputeRentalSnapshot(args: {
  date: string;
  capturedAt: string;
  vastPayload: unknown;
  runpodPayload: unknown;
  /** Optional only for constructing legacy schema-v1 fixtures. Production refreshes pass all catalog payloads. */
  azureCpuPayloads?: unknown[];
  /** Optional only for legacy fixtures; production uses dated aggregate snapshots from getflops.ai. */
  gpuPriceHistoryPayload?: unknown;
  vastQueryLimit?: number;
}): ComputeRentalSnapshot {
  assertCalendarDate(args.date, "Rental snapshot date");
  assertTimestamp(args.capturedAt, "Rental snapshot capturedAt");
  const queryLimit = args.vastQueryLimit ?? VAST_QUERY_LIMIT;
  finite(queryLimit, "Vast query limit", { min: 1, integer: true });
  const vast = parseVastOffers(args.vastPayload);
  const vastBySku = aggregateVast(vast.reliable);
  const runpod = parseRunpodGpuTypes(args.runpodPayload);
  const cpuRows = args.azureCpuPayloads == null ? undefined : parseAzureCpuRows(args.azureCpuPayloads, args.capturedAt);
  const gpuPriceHistory = args.gpuPriceHistoryPayload == null
    ? undefined
    : buildGpuRentalHistoryDataset(args.gpuPriceHistoryPayload, args.capturedAt);
  const rows: ComputeRentalGpuRow[] = RENTAL_GPU_CATALOG.map((catalog) => {
    const runpodRow = firstRunpodRow(runpod.byId, catalog.runpodIds);
    return {
      sku: catalog.sku,
      label: catalog.label,
      segment: catalog.segment,
      vramGb: catalog.vramGb,
      vast: vastBySku.get(catalog.sku) ?? null,
      runpodCommunity: runpodRow ? parseRunpodQuote(runpodRow.community, `RunPod ${runpodRow.id} community`) : null,
      runpodSecure: runpodRow ? parseRunpodQuote(runpodRow.secure, `RunPod ${runpodRow.id} secure`) : null,
    };
  });
  if (!rows.some((row) => row.vast) || !rows.some((row) => row.runpodCommunity || row.runpodSecure)) {
    throw new Error("Rental snapshot lacks comparable Vast or RunPod catalog quotes.");
  }
  const snapshot: ComputeRentalSnapshot = {
    schemaVersion: cpuRows ? 2 : 1,
    date: args.date,
    capturedAt: new Date(args.capturedAt).toISOString(),
    currency: "USD",
    rentalType: "on-demand",
    sample: {
      vastQueryLimit: queryLimit,
      vastOfferCount: vast.reliable.length,
      vastCatalogOfferCount: rows.reduce((sum, row) => sum + (row.vast?.offerCount ?? 0), 0),
      vastSampleTruncated: vast.sourceCount >= queryLimit,
      vastReliabilityFloor: VAST_RELIABILITY_FLOOR,
      runpodGpuTypeCount: runpod.count,
      ...(cpuRows ? { azureCpuSkuCount: cpuRows.length, azureCpuRegion: "eastus" as const } : {}),
    },
    rows,
    ...(cpuRows ? { cpuRows } : {}),
    ...(gpuPriceHistory ? { gpuPriceHistory } : {}),
  };
  assertSafeAggregate(snapshot);
  return snapshot;
}

function validateRunpodAggregate(value: unknown, label: string) {
  if (value == null) return;
  if (!isRecord(value) || typeof value.stockStatus !== "string" || !STOCK_STATUSES.has(value.stockStatus as RentalStockStatus)
    || !Number.isFinite(value.pricePerGpuHourUsd) || Number(value.pricePerGpuHourUsd) <= 0
    || (value.availableGpuCounts != null && (!Array.isArray(value.availableGpuCounts)
      || value.availableGpuCounts.some((count) => !Number.isInteger(count) || Number(count) <= 0)))) {
    throw new Error(`${label} is invalid.`);
  }
}

function validateVastAggregate(value: unknown, label: string) {
  if (value == null) return;
  if (!isRecord(value) || !Number.isInteger(value.offerCount) || Number(value.offerCount) <= 0
    || !Number.isInteger(value.gpuCount) || Number(value.gpuCount) <= 0 || !isRecord(value.pricePerGpuHourUsd)) {
    throw new Error(`${label} is invalid.`);
  }
  const prices = [value.pricePerGpuHourUsd.min, value.pricePerGpuHourUsd.p25, value.pricePerGpuHourUsd.median, value.pricePerGpuHourUsd.p75];
  if (prices.some((price) => !Number.isFinite(price) || Number(price) <= 0)
    || !(Number(prices[0]) <= Number(prices[1]) && Number(prices[1]) <= Number(prices[2]) && Number(prices[2]) <= Number(prices[3]))) {
    throw new Error(`${label} price distribution is invalid.`);
  }
  for (const field of ["medianEffectiveVcpuPerGpu", "medianCpuRamGbPerGpu", "medianBundleUsdPerEffectiveVcpuHour", "medianDlperfPerDollar", "medianFlopsPerDollar"] as const) {
    if (value[field] != null && (!Number.isFinite(value[field]) || Number(value[field]) <= 0)) throw new Error(`${label} ${field} is invalid.`);
  }
  if (!Number.isFinite(value.medianReliabilityPct) || Number(value.medianReliabilityPct) < VAST_RELIABILITY_FLOOR * 100 || Number(value.medianReliabilityPct) > 100) {
    throw new Error(`${label} reliability is invalid.`);
  }
}

function validateAzureCpuAggregate(value: unknown, catalog: AzureCpuCatalogRow, label: string) {
  if (!isRecord(value) || value.provider !== "azure" || value.region !== "eastus" || value.tier !== catalog.tier
    || value.sku !== catalog.sku || value.label !== catalog.label || value.vcpus !== catalog.vcpus || value.memoryGb !== catalog.memoryGb) {
    throw new Error(`${label} does not match the CPU catalog.`);
  }
  const price = finite(value.pricePerHourUsd, `${label} pricePerHourUsd`, { min: Number.MIN_VALUE });
  const perVcpu = finite(value.pricePerVcpuHourUsd, `${label} pricePerVcpuHourUsd`, { min: Number.MIN_VALUE });
  const perMemory = finite(value.pricePerMemoryGbHourUsd, `${label} pricePerMemoryGbHourUsd`, { min: Number.MIN_VALUE });
  if (Math.abs(perVcpu - price / catalog.vcpus) > 1e-9 || Math.abs(perMemory - price / catalog.memoryGb) > 1e-9) {
    throw new Error(`${label} unit prices do not reconcile to the hourly price.`);
  }
  assertTimestamp(value.effectiveStartDate, `${label} effectiveStartDate`);
}

function validateGpuRentalHistoryDataset(value: unknown): asserts value is GpuRentalHistoryDataset {
  if (!isRecord(value) || value.source !== "getflops" || value.sourceUrl !== "https://www.getflops.ai/api/history_all"
    || value.attribution !== "Historical GPU pricing: getflops.ai" || !Array.isArray(value.series) || value.series.length === 0) {
    throw new Error("GPU rental history dataset has an invalid schema or attribution.");
  }
  assertCalendarDate(value.firstDate, "GPU rental history firstDate");
  assertCalendarDate(value.lastDate, "GPU rental history lastDate");
  if (value.firstDate > value.lastDate) throw new Error("GPU rental history date range is reversed.");
  const validSkus = new Set(Object.keys(GETFLOPS_GPU_NAMES));
  const validProviders = new Set<RentalHistoryProvider>(GETFLOPS_PROVIDERS.map((item) => item.provider));
  const seen = new Set<string>();
  const dates: string[] = [];
  for (const [seriesIndex, item] of value.series.entries()) {
    if (!isRecord(item) || typeof item.sku !== "string" || !validSkus.has(item.sku)
      || typeof item.provider !== "string" || !validProviders.has(item.provider as RentalHistoryProvider)
      || !Array.isArray(item.points) || item.points.length < 2 || item.points.length > GPU_RENTAL_BACKFILL_DAYS) {
      throw new Error(`GPU rental history series ${seriesIndex + 1} is invalid.`);
    }
    const key = `${item.sku}|${item.provider}`;
    if (seen.has(key)) throw new Error(`GPU rental history series ${key} is duplicated.`);
    seen.add(key);
    let priorDate = "";
    for (const [pointIndex, point] of item.points.entries()) {
      const label = `GPU rental history ${key} point ${pointIndex + 1}`;
      if (!isRecord(point)) throw new Error(`${label} is invalid.`);
      assertCalendarDate(point.date, `${label} date`);
      assertTimestamp(point.capturedAt, `${label} capturedAt`);
      if ((point.capturedAt as string).slice(0, 10) !== point.date || point.date <= priorDate) throw new Error(`${label} is not unique and sorted.`);
      const min = finite(point.minPricePerGpuHourUsd, `${label} min`, { min: Number.MIN_VALUE });
      const median = finite(point.medianPricePerGpuHourUsd, `${label} median`, { min: Number.MIN_VALUE });
      const max = point.maxPricePerGpuHourUsd == null ? null : finite(point.maxPricePerGpuHourUsd, `${label} max`, { min: Number.MIN_VALUE });
      finite(point.offerCount, `${label} offerCount`, { min: 1, integer: true });
      if (min > median || (max != null && median > max)) throw new Error(`${label} price distribution is invalid.`);
      priorDate = point.date as string;
      dates.push(priorDate);
    }
  }
  if (!seen.has("h100-sxm|vast") || !seen.has("h100-sxm|runpod-secure")) {
    throw new Error("GPU rental history lacks required H100 SXM series.");
  }
  dates.sort();
  if (dates[0] !== value.firstDate || dates.at(-1) !== value.lastDate) throw new Error("GPU rental history range does not match its points.");
  assertSafeAggregate(value);
}

export function parseComputeRentalSnapshot(value: unknown): ComputeRentalSnapshot {
  if (!isRecord(value) || (value.schemaVersion !== 1 && value.schemaVersion !== 2) || value.currency !== "USD" || value.rentalType !== "on-demand"
    || !isRecord(value.sample) || !Array.isArray(value.rows) || value.rows.length !== RENTAL_GPU_CATALOG.length) {
    throw new Error("Compute rental snapshot has an invalid schema.");
  }
  assertCalendarDate(value.date, "Compute rental snapshot date");
  assertTimestamp(value.capturedAt, "Compute rental snapshot capturedAt");
  for (const field of ["vastQueryLimit", "vastOfferCount", "vastCatalogOfferCount", "runpodGpuTypeCount"] as const) {
    finite(value.sample[field], `Compute rental sample ${field}`, { min: field === "vastCatalogOfferCount" ? 0 : 1, integer: true });
  }
  if (typeof value.sample.vastSampleTruncated !== "boolean" || value.sample.vastReliabilityFloor !== VAST_RELIABILITY_FLOOR) {
    throw new Error("Compute rental sample metadata is invalid.");
  }
  const expected = new Map(RENTAL_GPU_CATALOG.map((row) => [row.sku, row]));
  const seen = new Set<string>();
  for (const [index, row] of value.rows.entries()) {
    if (!isRecord(row) || typeof row.sku !== "string" || seen.has(row.sku)) throw new Error(`Compute rental row ${index + 1} has an invalid or duplicate SKU.`);
    seen.add(row.sku);
    const catalog = expected.get(row.sku);
    if (!catalog || row.label !== catalog.label || row.segment !== catalog.segment || row.vramGb !== catalog.vramGb) {
      throw new Error(`Compute rental row ${row.sku} does not match the catalog.`);
    }
    validateVastAggregate(row.vast, `Compute rental row ${row.sku} Vast`);
    validateRunpodAggregate(row.runpodCommunity, `Compute rental row ${row.sku} RunPod community`);
    validateRunpodAggregate(row.runpodSecure, `Compute rental row ${row.sku} RunPod secure`);
  }
  if (seen.size !== expected.size) throw new Error("Compute rental snapshot catalog coverage is incomplete.");
  if (value.schemaVersion === 2) {
    if (value.sample.azureCpuSkuCount !== AZURE_CPU_CATALOG.length || value.sample.azureCpuRegion !== "eastus"
      || !Array.isArray(value.cpuRows) || value.cpuRows.length !== AZURE_CPU_CATALOG.length) {
      throw new Error("Compute rental snapshot CPU metadata is invalid.");
    }
    value.cpuRows.forEach((row, index) => validateAzureCpuAggregate(row, AZURE_CPU_CATALOG[index], `Compute rental CPU row ${index + 1}`));
  } else if (value.cpuRows != null || value.sample.azureCpuSkuCount != null || value.sample.azureCpuRegion != null) {
    throw new Error("Compute rental schema-v1 snapshot cannot contain Azure CPU rows.");
  }
  if (value.gpuPriceHistory != null) validateGpuRentalHistoryDataset(value.gpuPriceHistory);
  assertSafeAggregate(value);
  return value as unknown as ComputeRentalSnapshot;
}

export function mergeComputeRentalPanel(prior: ComputeRentalPanel | undefined, nextValue: unknown): ComputeRentalPanel {
  const next = parseComputeRentalSnapshot(nextValue);
  const history = prior ? parseComputeRentalPanel(prior).history : [];
  const byDate = new Map(history.map((snapshot) => [snapshot.date, snapshot]));
  const { gpuPriceHistory: nextGpuPriceHistory, ...nextDailySnapshot } = next;
  byDate.set(next.date, nextDailySnapshot);
  const merged = [...byDate.values()]
    .sort((left, right) => left.date.localeCompare(right.date))
    .slice(-COMPUTE_RENTAL_HISTORY_DAYS);
  return {
    asOf: next.date,
    history: merged,
    ...(nextGpuPriceHistory || prior?.gpuPriceHistory
      ? { gpuPriceHistory: nextGpuPriceHistory ?? prior?.gpuPriceHistory }
      : {}),
    methodology: [
      "Vast: verified, rentable, non-rented on-demand offers with reliability >= 95%; dph_total is divided by listed GPU count.",
      "RunPod: one-GPU lowest uninterruptable price, split into Community and Secure Cloud; null means no quoted stock.",
      "Azure CPU: East US Linux pay-as-you-go Virtual Machines only; Spot, Low Priority, Windows, and Cloud Services meters are excluded.",
      "GPU price history: getflops.ai dated collector snapshots, normalized to USD per GPU-hour and kept separate by provider and pricing type; current direct API snapshots overwrite the same calendar date in the chart.",
      "Daily snapshots are keyed by calendar date in R2; a same-day rerun replaces that day's observation instead of duplicating it.",
    ],
    limitations: [
      "Vast is a marketplace distribution while RunPod is a platform lowest price; compare the spread, do not average them into one market price.",
      "Vast bundled $/effective-vCPU-hour includes the GPU and system, so it is not a standalone CPU rental price.",
      "Azure D/E-series prices are a fixed public-cloud benchmark, not a market-wide CPU average; region, commitment term, and architecture can change realized cost.",
      "Listed supply and stock labels are availability signals, not utilization, contracted capacity, or guaranteed provisionability.",
      "Historical trends begin with collected R2 snapshots; neither provider exposes a complete point-in-time price history in these endpoints.",
      "Getflops history begins in March 2026 and is a third-party normalized collector, not a vendor billing record; attribution remains visible in the UI.",
    ],
  };
}

export function parseComputeRentalPanel(value: unknown): ComputeRentalPanel {
  if (!isRecord(value) || !Array.isArray(value.history) || value.history.length === 0 || value.history.length > COMPUTE_RENTAL_HISTORY_DAYS
    || !Array.isArray(value.methodology) || !Array.isArray(value.limitations)) {
    throw new Error("Compute rental panel has an invalid schema.");
  }
  assertCalendarDate(value.asOf, "Compute rental panel asOf");
  let priorDate = "";
  for (const snapshot of value.history) {
    const parsed = parseComputeRentalSnapshot(snapshot);
    if (parsed.date <= priorDate) throw new Error("Compute rental panel history must be unique and sorted.");
    priorDate = parsed.date;
  }
  if (priorDate !== value.asOf) throw new Error("Compute rental panel asOf must match the latest snapshot.");
  if (value.methodology.some((item) => typeof item !== "string" || !item) || value.limitations.some((item) => typeof item !== "string" || !item)) {
    throw new Error("Compute rental panel notes are invalid.");
  }
  if (value.gpuPriceHistory != null) validateGpuRentalHistoryDataset(value.gpuPriceHistory);
  assertSafeAggregate(value);
  return value as unknown as ComputeRentalPanel;
}
